import { useState } from 'react';
import type { ReactElement } from 'react';
import { paisa } from '@fleet/shared';
import { useVoidInvoiceMutation } from '@/api/endpoints';
import type { Invoice } from '@/api/types';
import { Button } from '@/design/Button';
import { MoneyText } from '@/design/Data';
import { Field } from '@/design/Field';
import { Modal } from '@/design/Modal';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';
import { printElement } from '@/lib/printElement';

/**
 * The invoice itself, as the customer sees it.
 *
 * Deliberately laid out as a document rather than a form: a heading with the
 * business's own details, the lines, then the totals. When somebody rings about
 * an invoice, this is what they are holding, and matching it line for line is
 * the whole job of this screen.
 *
 * **The rates include VAT**, so the totals read the way the client's price list
 * does: the total is what was quoted, and the tax is shown as what is *inside*
 * it. "Of which VAT" rather than "plus VAT" — get that wrong and every figure
 * on the page is about 3% out.
 *
 * Printing uses the browser's own print, with a stylesheet that drops
 * everything except the document. No PDF library, and nothing to install on a
 * counter machine.
 */

interface Props {
  readonly invoice: Invoice | null;
  readonly onClose: () => void;
}

export function InvoiceModal({ invoice, onClose }: Props): ReactElement {
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [voidInvoice, saving] = useVoidInvoiceMutation();
  const toast = useToast();
  const problems = readApiError(saving.error);

  async function doVoid(): Promise<void> {
    if (invoice === null) return;
    try {
      await voidInvoice({ id: invoice.id, reason }).unwrap();
      toast.done(`${invoice.number} is void. Raise a new one to replace it.`);
      setVoiding(false);
      setReason('');
      onClose();
    } catch {
      // Stays in the modal — it is about the reason that was typed.
    }
  }

  const isVoid = invoice?.status === 'void';

  return (
    <Modal
      open={invoice !== null}
      onClose={onClose}
      size="wide"
      title={invoice?.number ?? 'Invoice'}
      note={
        invoice === null
          ? undefined
          : isVoid
            ? `Void — ${invoice.void_reason}`
            : `Issued ${invoice.issued_at_bs ?? ''} · ${invoice.customer_name}`
      }
      footer={
        <>
          {/*
            "PDF is not supported" was reported against this screen. It is —
            the browser's own print dialog has "Save as PDF" as a destination,
            and that is what produces the file people email to customers. The
            button said "Print", so nobody found it.

            Deliberately still the browser's print rather than a PDF library:
            a client-side generator is ~300KB on a counter machine on mobile
            data, and a server-side one is a headless browser to install and
            keep patched. Neither buys anything over a destination the
            operating system already provides.
          */}
          <Button intent="primary" size="touch" onClick={() => printElement('printable-invoice')}>
            Print or save as PDF
          </Button>
          {!isVoid && !voiding && (
            <Button intent="danger" size="touch" onClick={() => setVoiding(true)}>
              Void this invoice
            </Button>
          )}
          <Button size="touch" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {invoice === null ? null : (
        <div className="flex flex-col gap-5">
          {voiding && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void doVoid();
              }}
              className="flex flex-col gap-3 border border-alert/40 border-l-[3px] border-l-alert bg-alert-wash p-4"
            >
              <p className="font-medium text-alert">
                Voiding keeps the number. {invoice.number} will not be reused, and a replacement
                gets the next one.
              </p>
              <Field label="Why" required error={problems.fields['reason'] ?? problems.detail}>
                {({ id, describedBy }) => (
                  <input
                    id={id}
                    aria-describedby={describedBy}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Wrong dates on the rental line."
                    className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
                  />
                )}
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button
                  intent="danger"
                  type="submit"
                  disabled={saving.isLoading || reason.trim().length < 6}
                >
                  {saving.isLoading ? 'Voiding' : 'Void it'}
                </Button>
                <Button intent="quiet" onClick={() => setVoiding(false)}>
                  Keep it
                </Button>
              </div>
            </form>
          )}

          {/* --- The document ------------------------------------------- */}
          <article id="printable-invoice" className="border border-rule bg-white p-5 sm:p-6">
            <header className="flex flex-wrap items-start justify-between gap-4 border-b border-rule pb-4">
              <div className="min-w-0">
                <h3 className="text-heading font-semibold">{invoice.business_name}</h3>
                {invoice.business_address !== '' && (
                  <p className="whitespace-pre-line text-small text-ink-muted">
                    {invoice.business_address}
                  </p>
                )}
                {invoice.business_pan !== '' && (
                  <p className="text-small text-ink-muted">
                    PAN <span className="identifier">{invoice.business_pan}</span>
                  </p>
                )}
              </div>

              <dl className="text-right text-small">
                <dt className="text-micro uppercase text-ink-muted">Tax invoice</dt>
                <dd className="identifier text-body font-semibold">{invoice.number}</dd>
                {invoice.issued_at !== null && (
                  <>
                    <dd className="tnum mt-1">
                      {new Date(invoice.issued_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </dd>
                    <dd className="text-ink-muted">{invoice.issued_at_bs} BS</dd>
                  </>
                )}
              </dl>
            </header>

            <div className="flex flex-wrap justify-between gap-4 py-4">
              <div>
                <p className="text-micro uppercase text-ink-muted">Billed to</p>
                <p className="font-medium">{invoice.customer_name}</p>
                <p className="identifier text-small text-ink-muted">{invoice.customer_phone}</p>
                {/* Only when there is one. A blank "PAN:" line on a tourist's
                    bill reads as something missing rather than something that
                    does not apply. */}
                {invoice.customer_pan !== '' && (
                  <p className="text-small text-ink-muted">
                    PAN <span className="identifier">{invoice.customer_pan}</span>
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-micro uppercase text-ink-muted">Against booking</p>
                <p className="identifier">{invoice.booking_reference}</p>
              </div>
            </div>

            <table className="w-full border-t border-rule text-small">
              <thead>
                <tr className="border-b border-rule">
                  <th className="py-2 text-left text-micro uppercase text-ink-muted">
                    Description
                  </th>
                  <th className="py-2 text-right text-micro uppercase text-ink-muted">Qty</th>
                  <th className="py-2 text-right text-micro uppercase text-ink-muted">Rate</th>
                  <th className="py-2 text-right text-micro uppercase text-ink-muted">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line) => (
                  <tr key={line.id} className="border-b border-rule">
                    <td className="py-2 pr-2">{line.description}</td>
                    <td className="tnum py-2 text-right">{line.quantity}</td>
                    <td className="tnum py-2 text-right">
                      <MoneyText amount={paisa(line.unit_amount)} />
                    </td>
                    <td className="tnum py-2 text-right">
                      <MoneyText amount={paisa(line.amount)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex justify-end">
              <dl className="w-full max-w-xs text-small">
                <div className="flex justify-between py-1">
                  <dt>Sub Total</dt>
                  <dd className="tnum">
                    <MoneyText amount={paisa(invoice.subtotal)} />
                  </dd>
                </div>

                {invoice.discount > 0 && (
                  <div className="flex justify-between py-1">
                    <dt>Discount</dt>
                    <dd className="tnum">
                      −<MoneyText amount={paisa(invoice.discount)} />
                    </dd>
                  </div>
                )}

                <div className="flex justify-between border-t border-rule-strong py-2">
                  <dt className="font-semibold">Total</dt>
                  <dd className="tnum text-heading font-semibold">
                    <MoneyText amount={paisa(invoice.total)} />
                  </dd>
                </div>

                {/* The client's own wording, word for word. A customer holding
                    the bill asks how much is left to pay, and until now the
                    bill could not answer. These two are the state of the
                    account rather than part of the tax figures above, so they
                    are live: a reprint after settling says nothing remains. */}
                <div className="flex justify-between py-1">
                  <dt>Advance Received</dt>
                  <dd className="tnum">
                    <MoneyText amount={paisa(invoice.advance_received)} />
                  </dd>
                </div>
                <div className="flex justify-between border-t border-rule py-2">
                  <dt className="font-semibold">Balance Remaining</dt>
                  <dd className="tnum font-semibold">
                    <MoneyText amount={paisa(invoice.balance_remaining)} />
                  </dd>
                </div>

                {invoice.vat > 0 && (
                  <>
                    {/* Inclusive pricing: these two explain the total rather
                        than adding to it, so they sit below the line. */}
                    <div className="flex justify-between py-1 text-ink-muted">
                      <dt>Taxable value</dt>
                      <dd className="tnum">
                        <MoneyText amount={paisa(invoice.taxable_amount)} />
                      </dd>
                    </div>
                    <div className="flex justify-between py-1 text-ink-muted">
                      <dt>Of which VAT (13%)</dt>
                      <dd className="tnum">
                        <MoneyText amount={paisa(invoice.vat)} />
                      </dd>
                    </div>
                    <p className="mt-2 text-micro text-ink-muted">
                      Rates include VAT. The total is what the customer pays.
                    </p>
                  </>
                )}
              </dl>
            </div>

            {/*
              The stamp and the signature line, where they go on a real bill.

              Copied onto the invoice when it was raised rather than read from
              the business's current settings, so changing the stamp next year
              does not change what is printed on a document somebody is already
              holding — the same promise every figure above it makes.

              An `<img>` rather than a CSS background on purpose: browsers do
              not print background images by default, and a stamp that vanishes
              on paper is worse than no stamp at all.
            */}
            <div className="mt-6 flex items-end justify-between gap-6">
              <p className="text-micro text-ink-muted">
                {invoice.status === 'issued'
                  ? 'Thank you for your custom.'
                  : 'Draft — not yet issued.'}
              </p>

              <div className="flex flex-col items-center">
                {invoice.business_stamp_url !== '' && (
                  <img
                    src={invoice.business_stamp_url}
                    alt=""
                    width={80}
                    height={80}
                    className="-mb-5 h-20 w-20 object-contain"
                  />
                )}
                <span className="w-48 border-t border-ink pt-1 text-center text-micro text-ink-muted">
                  For {invoice.business_name}
                </span>
              </div>
            </div>

            {isVoid && (
              <p className="mt-4 border-t border-rule pt-3 font-semibold text-alert">
                VOID — {invoice.void_reason}
              </p>
            )}
          </article>
        </div>
      )}
    </Modal>
  );
}
